/**
 * This module processes server change log entries to create a timeline of changes.
 * It formats dates, maps server actions to user-friendly strings, and handles different types of updates.
 * Each log entry is processed and categorized by date, and additional data is included based on the type of action.
 */

import dayjs from "dayjs";
import {
  ChangelogDate,
  ChangelogEntry,
  ChangeLogUpdates,
  ServerChangeLogEntry,
  ServerEscalationCaseDetails,
  ServerCaseItemDetails,
  ServerWorkPacketProperties,
  type WorkPacketActionName,
} from "src/types/work-packets";
import { WorkPacketType } from "../WorkPacketType";
import { getActionObject } from "./mapActionNameToPacketActionObject";
import { attempt } from "src/utils/attempt";
import {
  CorrespondenceWorkFlag,
  correspondenceWorkFlagLabels,
} from "src/pages/UserDashboard/WorkPackets/Cases/correspondence-work-flags.ts";
import {
  CaseStatus,
  getStatusOption,
  DeniedReason,
  getDeniedReasonOption,
  allShortagesCaseItemStatusOptions,
  chargebackEscalationCaseStagesMap,
  AbandonedReason,
  getAbandonedReasonOption,
} from "src/pages/UserDashboard/Cases/api/constants";
import { EMPTY_ARRAY } from "src/utils/empty-values";

/**
 * Formats a date string into "Today" if the date is the current date, otherwise returns "MMM DD" format.
 */
function formatTimelineDate(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    return "Today";
  } else {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }
}

const formatDate = (dateString: string) => attempt(() => dayjs(dateString).format("MMM DD, YYYY"), dateString);

type ChangeLogActionType =
  | "ASSIGNED"
  | "UNASSIGNED"
  | "IDENTIFIED"
  | "UPDATED"
  | "LINKED"
  | "UNLINKED"
  | "FLAGGED"
  | "UNFLAGGED";

/**
 * These are the fields with user objects that might show up at the root
 * of a server change log entry when actions is `ASSIGNED`.
 */
enum AssignedUserKind {
  PACKET_OWNER = "PACKET_OWNER",
  BACKUP_PACKET_OWNER = "BACKUP_PACKET_OWNER",
  MONITORING_PACKET_OWNER = "MONITORING_PACKET_OWNER",
  CASE_OWNER = "CASE_OWNER",
  BACKUP_CASE_OWNER = "BACKUP_CASE_OWNER",
  PROCESSOR = "PROCESSOR",
  BACKUP_PROCESSOR = "BACKUP_PROCESSOR",
}

type AssignedUserIdKey = `${AssignedUserKind}_ID`;

interface ServerChangeLogAction {
  type: ChangeLogActionType;
  assignedUserKind?: AssignedUserKind;
}

const assignedUserKindLabels: Record<AssignedUserKind, string> = {
  [AssignedUserKind.PACKET_OWNER]: "Packet Owner",
  [AssignedUserKind.BACKUP_PACKET_OWNER]: "Backup Packet Owner",
  [AssignedUserKind.MONITORING_PACKET_OWNER]: "Monitoring Packet Owner",
  [AssignedUserKind.CASE_OWNER]: "Case Owner",
  [AssignedUserKind.BACKUP_CASE_OWNER]: "Backup Case Owner",
  [AssignedUserKind.PROCESSOR]: "Processor",
  [AssignedUserKind.BACKUP_PROCESSOR]: "Backup Processor",
};

const mapServerAction = (action: ServerChangeLogAction, type: string): string => {
  if (action.assignedUserKind) {
    const assignedUserKind = action.assignedUserKind;
    return `Assigned the ${assignedUserKindLabels[assignedUserKind]}`;
  }
  const actions: Record<string, string> = {
    ASSIGNED: `Assigned the ${type}`,
    UNASSIGNED: `Unassigned the ${type}`,
    IDENTIFIED: `Identified the ${type}`,
    UPDATED: "Changed",
    LINKED: "Linked the",
    UNLINKED: "Unlinked the",
    FLAGGED: "",
    UNFLAGGED: "",
  };
  return actions[action.type];
};

type UpdateMapper<T> = (
  value: NonNullable<T>,
  log: ServerChangeLogEntry,
  workPacketType: WorkPacketType,
) => Partial<ChangelogEntry>[];

type UpdateFieldKey = keyof ChangeLogUpdates;

type UpdateMappers = Required<{
  [FieldKey in UpdateFieldKey]: UpdateMapper<ChangeLogUpdates[FieldKey]>;
}>;

const emptyMapper: UpdateMapper<unknown> = () => EMPTY_ARRAY;

const escalationCaseDetailsMapper = (value: ServerEscalationCaseDetails) =>
  Object.entries(value).reduce((entries, [key, value]) => {
    if (key === "APPROVED_AMOUNT") {
      return [...entries, { state: "Approved Amount", to: value }];
    }
    if (key === "APPROVED_DATE") {
      return [...entries, { state: "Approved At", to: formatDate(value) }];
    }
    if (key === "MONITORED_DATE") {
      return [...entries, { state: "Monitored At", to: formatDate(value) }];
    }
    return entries;
  }, [] as Partial<ChangelogEntry>[]);

const caseItemDetailsMapper = (value: ServerCaseItemDetails) =>
  Object.entries(value).reduce((entries, [key, value]) => {
    if (key === "STATUS") {
      const statusOption = [...allShortagesCaseItemStatusOptions, ...chargebackEscalationCaseStagesMap].find(
        s => s.value === value,
      );
      return [...entries, { state: "Case Item Status", to: statusOption?.title || value }];
    }
    return entries;
  }, [] as Partial<ChangelogEntry>[]);

const workPacketPropertiesMapper = (value: ServerWorkPacketProperties) =>
  Object.entries(value).reduce((entries, [key, value]) => {
    if (key === "DENIED_REASON") {
      return [...entries, { state: "Denied Reason", to: getDeniedReasonOption(value as DeniedReason).title }];
    }
    if (key === "ABANDONED_REASON") {
      return [
        ...entries,
        {
          state: "Abandoned Reason",
          to: value ? getAbandonedReasonOption(value as AbandonedReason).title : "Unabandoned (field cleared)",
        },
      ];
    }
    return entries;
  }, [] as Partial<ChangelogEntry>[]);
const createFallbackMapper =
  (key: string): UpdateMapper<unknown> =>
  value => [
    {
      state: key,
      to: JSON.stringify(value),
    },
  ];

const updateMappers: UpdateMappers = {
  // These are user object fields that always come with separate ID fields
  // We skip the user object fields and create changelogs based on the ID fields instead
  PACKET_OWNER: emptyMapper,
  BACKUP_PACKET_OWNER: emptyMapper,
  CASE_OWNER: emptyMapper,
  BACKUP_CASE_OWNER: emptyMapper,
  MONITORING_PACKET_OWNER: emptyMapper,
  PROCESSOR: emptyMapper,
  BACKUP_PROCESSOR: emptyMapper,

  // TODO(daniel): flesh out BULK_ACTION changelogs
  BULK_ACTION: value => [
    {
      action: `Performed bulk update: ${JSON.stringify(value)}`,
    },
  ],

  PACKET_OWNER_ID: (value, log) => [
    {
      state: "Packet Owner",
      to: log.PACKET_OWNER?.NICKNAME || log.UPDATES.PACKET_OWNER?.NICKNAME || value,
    },
  ],
  BACKUP_PACKET_OWNER_ID: (value, log) => [
    {
      state: "Backup Packet Owner",
      to: log.UPDATES.BACKUP_PACKET_OWNER?.NICKNAME || value,
    },
  ],
  MONITORING_PACKET_OWNER_ID: (value, log) => [
    {
      state: "Monitoring Packet Owner",
      to: log.UPDATES.MONITORING_PACKET_OWNER?.NICKNAME || value,
    },
  ],
  AMAZON_CASE_ID: value => [
    {
      state: "Amazon Case ID",
      to: value,
    },
  ],
  PROCESSOR_ID: (value, log) => [
    {
      state: "Processor",
      to: log.UPDATES.PROCESSOR?.NICKNAME || value,
    },
  ],
  BACKUP_PROCESSOR_ID: (value, log) => [
    {
      state: "Backup Processor",
      to: log.UPDATES.BACKUP_PROCESSOR?.NICKNAME || value,
    },
  ],
  CURRENT_ACTION: (value, _log, workPacketType) => [
    {
      state: "Packet State",
      to: getActionObject(value as WorkPacketActionName, workPacketType).title,
    },
  ],
  NOTES: value => [
    {
      state: "Notes",
      to: value,
    },
  ],
  SYSTEM_NOTES: value => [
    {
      state: "System Notes",
      to: value,
    },
  ],
  VALID: value => [
    {
      state: "Chargeback Validity",
      to: value ? "Valid" : "Invalid",
    },
  ],
  CASE_ID: (value, log) => {
    if (log.ACTION === "LINKED") {
      return [
        {
          state: "work packet to case",
          value,
        },
      ];
    } else if (log.ACTION === "UNLINKED") {
      return [
        {
          state: "work packet from case",
          value,
        },
      ];
    }
    return [
      {
        state: "Case ID",
        value,
      },
    ];
  },
  CASE_OWNER_ID: (value, log) => [
    {
      state: "Case Owner",
      to: log.UPDATES.CASE_OWNER?.NICKNAME || value,
    },
  ],
  CASE_FILING_USER: value => [
    {
      state: "Case Filing User",
      to: value,
    },
  ],
  AMAZON_CASE_CREATED_DATE: value => [
    {
      state: "Case Created Date",
      to: formatDate(value),
    },
  ],
  BACKUP_CASE_OWNER_ID: (value, log) => [
    {
      state: "Backup Packet Owner",
      to: log.UPDATES.BACKUP_CASE_OWNER?.NICKNAME || value,
    },
  ],
  STATUS: value =>
    value.toLowerCase() === "verified"
      ? [
          {
            state: "Case",
            to: "Verified",
          },
        ]
      : [
          {
            state: "Case Status",
            to: getStatusOption(value as CaseStatus).title,
          },
        ],
  CHARGEBACKS_ESCALATION_CASE_DETAILS: escalationCaseDetailsMapper,
  SHORTAGES_ESCALATION_CASE_DETAILS: escalationCaseDetailsMapper,
  SHORTAGES_ESCALATION_CASE_ITEM_DETAILS: caseItemDetailsMapper,
  CHARGEBACKS_ESCALATION_CASE_ITEM_DETAILS: caseItemDetailsMapper,
  WORK_PACKET_PROPERTIES: workPacketPropertiesMapper,
  WORK_PACKETS: (value, log) => {
    if (log.ACTION === "LINKED") {
      return value.map(work_packet => ({
        state: "case to work packet",
        value: work_packet,
      }));
    }
    if (log.ACTION === "UNLINKED") {
      return value.map(work_packet => ({
        state: "case from work packet",
        value: work_packet,
      }));
    }
    return [
      {
        state: "work packet on case",
      },
    ];
  },
  DISPUTE_ID: (value, log) => [
    {
      state: log.CASE_ID ? "Re-Dispute ID" : "Original Disputed ID",
      to: value,
    },
  ],
  CONSOLIDATED_DISPUTE_ID: value => [
    {
      state: "Consolidated Disputed ID",
      to: value,
    },
  ],
  PAID_AMOUNT: (value, log) => [
    {
      state: "Paid Amount",
      to: String(log.UPDATES.PAID_AMOUNT ?? value ?? ""),
    },
  ],
  DISPUTE_AMOUNT: (value, log) => [
    {
      state: "Dispute Amount",
      to: String(log.UPDATES.DISPUTE_AMOUNT ?? value ?? ""),
    },
  ],
  DISPUTE_CREATED_DATE: (value, log) => [
    {
      state: "Dispute Created Date",
      to: formatDate(log.UPDATES.DISPUTE_CREATED_DATE ?? value ?? ""),
    },
  ],
  CORRESPONDENCE_WORK_FLAG: (value, log) => {
    if (log.ACTION === "FLAGGED") {
      return [
        {
          state: "Case received a new correspondence work flag - ",
          value: correspondenceWorkFlagLabels[value as CorrespondenceWorkFlag],
        },
      ];
    }
    if (log.ACTION === "UNFLAGGED") {
      return [
        {
          state: "Cleared a correspondence work flag - ",
          value: correspondenceWorkFlagLabels[value as CorrespondenceWorkFlag],
        },
      ];
    }
    return [
      {
        state: "Case correspondence work flags changed",
      },
    ];
  },
  DENIED_REASON: value => [
    {
      state: "Denied Reason",
      to: getDeniedReasonOption(value as DeniedReason).title,
    },
  ],
  ABANDONED_REASON: value => [
    {
      state: "Abandoned Reason",
      to: value ? getAbandonedReasonOption(value as AbandonedReason).title : "Unabandoned (field cleared)",
    },
  ],
};

/**
 * Creates a changelog entry with the given log data, time, and additional data.
 */
const createEntry = (
  serverLog: ServerChangeLogEntry,
  time: string,
  originalDate: Date,
  isSystem: boolean,
  additionalData: Partial<ChangelogEntry> = {},
  assignedUserKind?: ServerChangeLogAction["assignedUserKind"],
): ChangelogEntry => {
  const changeLogType = serverLog.CASE_ID ? "case" : "work packet";

  const serverAction: ServerChangeLogAction = { type: serverLog.ACTION, assignedUserKind };

  const entry: ChangelogEntry = {
    time,
    originalDate,
    user: isSystem ? "System auto update" : serverLog.USER?.NICKNAME || "",
    action: mapServerAction(serverAction, changeLogType),
    isSystem,
    ...additionalData,
  };

  if (!isSystem && serverLog.USER?.PROFILE_PIC) {
    entry.avatarUrl = serverLog.USER.PROFILE_PIC;
  }

  return entry;
};

/**
 * Handles the updates for a log entry, creating separate changelog entries for each update.
 * @returns An array of changelog entries for the updates.
 */
const mapServerUpdateToLocalUpdates = (
  serverLog: ServerChangeLogEntry,
  time: string,
  isSystem: boolean,
  workPacketType: WorkPacketType,
  originalDate: Date,
): ChangelogEntry[] => {
  return Object.entries(serverLog.UPDATES).flatMap(([key, value]) => {
    const specificMapper = updateMappers[key as UpdateFieldKey] as UpdateMapper<ChangeLogUpdates[UpdateFieldKey]>;
    if (!specificMapper) {
      console.error(`No mapper found for updated field: ${key}`);
    }
    const mapper = specificMapper ?? createFallbackMapper(key);
    return mapper(value as NonNullable<ChangeLogUpdates[UpdateFieldKey]>, serverLog, workPacketType).map(partialLog => {
      partialLog.originalDate = originalDate;
      return createEntry(serverLog, time, originalDate, isSystem, partialLog);
    });
  });
};

/**
 * Processes a single log entry, creating and categorizing changelog entries by date.
 * @param serverLog - The server change log entry.
 * @param entriesByDate - An object categorizing entries by date.
 */
const processLogEntry = (
  serverLog: ServerChangeLogEntry,
  entriesByDate: Record<string, ChangelogEntry[]>,
  workPacketType: WorkPacketType,
) => {
  const date = formatTimelineDate(serverLog.RECORDED_AT);
  const time = new Date(serverLog.RECORDED_AT).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const originalDate = new Date(serverLog.RECORDED_AT);

  const isSystem = serverLog.USER?.NICKNAME === "systemuser";

  if (
    serverLog.ACTION === "LINKED" ||
    serverLog.ACTION === "UPDATED" ||
    serverLog.ACTION === "UNLINKED" ||
    serverLog.ACTION === "FLAGGED" ||
    serverLog.ACTION === "UNFLAGGED"
  ) {
    if (Object.keys(serverLog.UPDATES).length === 0) return;
    const updateEntries = mapServerUpdateToLocalUpdates(serverLog, time, isSystem, workPacketType, originalDate);
    entriesByDate[date] = entriesByDate[date] || [];
    entriesByDate[date].push(...updateEntries);
    return;
  }

  const additionalData: Partial<ChangelogEntry> = {};
  if (serverLog.ACTION === "IDENTIFIED") {
    additionalData.packetId = serverLog.WORK_PACKET_ID;
  }

  let assignedUserKind: AssignedUserKind | undefined;
  if (serverLog.ACTION === "ASSIGNED") {
    const changedKeys = Object.keys(serverLog.UPDATES);
    if (changedKeys.length !== 1) {
      console.error(`Unexpected number of changed keys for ASSIGNED action: ${changedKeys.length} (expected 1)`);
      return;
    }
    const changedUserIdKey: AssignedUserIdKey = changedKeys[0] as AssignedUserIdKey;
    const changedUserObjectKey: AssignedUserKind = changedUserIdKey.replace(/_ID$/, "") as AssignedUserKind;
    if (!Object.values(AssignedUserKind).includes(changedUserObjectKey)) {
      console.error(`Unexpected changed user object key for ASSIGNED action: ${changedUserIdKey}`);
      return;
    }
    assignedUserKind = changedUserObjectKey;
    additionalData.to =
      changedUserObjectKey in serverLog
        ? serverLog[changedUserObjectKey]?.NICKNAME
        : serverLog.UPDATES[changedUserIdKey];

    if (serverLog.UPDATES.PACKET_OWNER_ID === null || serverLog.UPDATES.PACKET_OWNER_ID === null) {
      serverLog.ACTION = "UNASSIGNED";
    }
  }

  const entry = createEntry(serverLog, time, originalDate, isSystem, additionalData, assignedUserKind);
  entriesByDate[date] = entriesByDate[date] || [];
  entriesByDate[date].push(entry);
};

export const mapServerChangeLog = ({
  serverChangeLog,
  workPacketType,
  isCaseChangeLog = false,
}: {
  serverChangeLog: ServerChangeLogEntry[];
  workPacketType: WorkPacketType;
  isCaseChangeLog?: boolean;
}): ChangelogDate[] => {
  const entriesByDate: Record<string, ChangelogEntry[]> = {};
  serverChangeLog.forEach(log => processLogEntry(log, entriesByDate, workPacketType));

  if (isCaseChangeLog) {
    const firstLogDate = formatTimelineDate(serverChangeLog.at(-1)!.RECORDED_AT);
    const firstParsedLog = entriesByDate[firstLogDate].at(-1)!;

    const identifiedLogIndex = serverChangeLog.findIndex(log => log.ACTION === "IDENTIFIED");
    if (identifiedLogIndex >= 0) {
      const originalLog = serverChangeLog[identifiedLogIndex];
      const modifiedLog = {
        action: `Case created - case ID ${originalLog.UPDATES.AMAZON_CASE_ID}`,
        time: firstParsedLog.time,
        user: firstParsedLog.user,
        originalDate: firstParsedLog.originalDate,
        avatarUrl: firstParsedLog.avatarUrl,
      };
      entriesByDate[firstLogDate].splice(identifiedLogIndex, 1, modifiedLog);
    }
  }

  return Object.keys(entriesByDate)
    .filter(date => entriesByDate[date].length > 0)
    .map(date => ({
      date,
      entries: entriesByDate[date],
    }));
};
